import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'

const useBroadcastRedeemStakeMutation = ({ redeemStakeRaw }: MutationParameters) => {
  const { getSigningStargateClient } = useStrideChain()

  const client = useQueryClient()

  const handleMutation = async (): Promise<DeliverTxResponse> => {
    assert(redeemStakeRaw, 'Unable to broadcast `RedeemStake` transaction without a signed document. Please try again.')

    const bytes = TxRaw.encode(redeemStakeRaw).finish()

    // Broadcast via stride signing client from cosmos-kit
    const client = await getSigningStargateClient()

    const tx = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(tx)

    return tx
  }

  const handleSettled = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase }),
      client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })
    ])
  }

  const handleSuccess = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.transactionHistoryUnbondingsBase }),
      // We'll update this for precautionary measures
      client.invalidateQueries({ queryKey: queryKeys.unstakeFormEpochTrackerBase }),
      // We'll invalidate redemption records as epoch tracker may not necessarily change
      client.invalidateQueries({ queryKey: queryKeys.unstakeFormRedemptionRecordBase })
    ])
  }

  return useMutation({
    mutationFn: handleMutation,
    onSettled: handleSettled,
    onSuccess: handleSuccess
  })
}

export { useBroadcastRedeemStakeMutation }
